import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, SectionLabel } from "../components/Motion";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES_CHAPE = [
  "Chape traditionnelle",
  "Chape allégée",
  "Béton dur",
  "Rampe d'accès garage",
  "Isolation thermique",
  "Isolation phonique",
  "Je ne sais pas / Conseil",
];

const TYPES_CHANTIER = ["Neuf", "Rénovation", "Extension"];
const DELAIS = ["Urgent (< 2 semaines)", "1 mois", "1 à 3 mois", "Plus de 3 mois", "Non défini"];

const initial = {
  nom: "",
  email: "",
  telephone: "",
  type_chape: "",
  surface_m2: "",
  type_chantier: "",
  adresse_chantier: "",
  delai: "",
  message: "",
};

export default function Devis() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.nom ||
      !form.email ||
      !form.telephone ||
      !form.type_chape ||
      !form.surface_m2 ||
      !form.type_chantier ||
      !form.adresse_chantier ||
      !form.delai
    ) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/devis`, {
        ...form,
        surface_m2: parseFloat(form.surface_m2),
      });
      setSuccess(true);
      setForm(initial);
      toast.success("Demande envoyée. Nous revenons vers vous rapidement.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Une erreur est survenue.";
      toast.error(typeof detail === "string" ? detail : "Erreur d'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-devis">
      <SEO
        title="Devis chape gratuit à Mulhouse — Devis sous 48h"
        description="Demandez votre devis gratuit pour chape traditionnelle, chape allégée, béton dur, isolation ou pose de parquet. Réponse sous 48h. Euro Chape, chapiste à Illzach près de Mulhouse."
        keywords="devis chape Mulhouse, devis chapiste Alsace, devis gratuit chape, prix chape Mulhouse, tarif chape Illzach"
        canonical="https://eurochape.com/devis"
      />
      <section className="pt-32 md:pt-40 pb-12 px-6 md:px-12 lg:px-24 bg-[#EBEBE6] border-b border-[#D5D5D0]">
        <Reveal>
          <SectionLabel>Devis gratuit</SectionLabel>
          <h1 className="font-display font-black uppercase tracking-tighter mt-8 text-5xl sm:text-7xl lg:text-[7rem] leading-[0.88]">
            Votre projet,
            <br />
            <span className="text-[#F08722]">notre devis.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed">
            Décrivez-nous votre chantier. Nous revenons vers vous sous 48h avec
            un devis détaillé et sans engagement.
          </p>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="bg-[#1A2D7E] text-white p-8 md:p-10 sticky top-28">
                <SectionLabel className="text-[#9A9A95]">Étapes</SectionLabel>
                <ol className="mt-8 space-y-6">
                  {[
                    "Vous décrivez votre projet",
                    "Nous étudions et chiffrons",
                    "Devis détaillé sous 48h",
                    "Visite technique si nécessaire",
                  ].map((s, i) => (
                    <li key={s} className="flex gap-4">
                      <span className="font-display font-black text-[#F08722] text-xl w-8">
                        0{i + 1}
                      </span>
                      <span className="font-light text-[#E6E6E0] text-sm leading-relaxed pt-1">
                        {s}
                      </span>
                    </li>
                  ))}
                </ol>
                <div className="mt-10 pt-8 border-t border-[#2D3F8E] text-xs uppercase tracking-[0.22em] text-[#9A9A95]">
                  Ou par téléphone
                </div>
                <a
                  href="tel:+33767945459"
                  className="block font-display font-bold text-2xl mt-3"
                >
                  07 67 94 54 59
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            {success ? (
              <Reveal>
                <div
                  data-testid="devis-success"
                  className="bg-white border border-[#D5D5D0] p-10 md:p-16 text-center"
                >
                  <CheckCircle2 className="w-14 h-14 text-[#F08722] mx-auto" strokeWidth={1.5} />
                  <h2 className="font-display font-bold uppercase tracking-tighter text-3xl md:text-4xl mt-6">
                    Demande envoyée
                  </h2>
                  <p className="mt-4 text-[#5C5E58] max-w-md mx-auto">
                    Merci pour votre confiance. Nous revenons vers vous sous 48h
                    avec un devis détaillé.
                  </p>
                  <button
                    data-testid="devis-new-btn"
                    onClick={() => setSuccess(false)}
                    className="mt-8 inline-flex items-center gap-3 border border-[#1A2D7E] text-[#1A2D7E] px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#1A2D7E] hover:text-white transition-colors"
                  >
                    Nouvelle demande
                  </button>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form
                  onSubmit={submit}
                  data-testid="devis-form"
                  className="bg-white border border-[#D5D5D0] p-8 md:p-12 space-y-10"
                >
                  <Fieldset legend="Le chantier">
                    <Select
                      label="Type de prestation *"
                      value={form.type_chape}
                      onChange={setField("type_chape")}
                      options={TYPES_CHAPE}
                      testid="field-type-chape"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        type="number"
                        label="Surface (m²) *"
                        value={form.surface_m2}
                        onChange={setField("surface_m2")}
                        testid="field-surface"
                        placeholder="120"
                        min="1"
                      />
                      <Select
                        label="Type de chantier *"
                        value={form.type_chantier}
                        onChange={setField("type_chantier")}
                        options={TYPES_CHANTIER}
                        testid="field-type-chantier"
                      />
                    </div>
                    <Input
                      label="Adresse du chantier *"
                      value={form.adresse_chantier}
                      onChange={setField("adresse_chantier")}
                      placeholder="Rue, code postal, ville"
                      testid="field-adresse"
                    />
                    <Select
                      label="Délai souhaité *"
                      value={form.delai}
                      onChange={setField("delai")}
                      options={DELAIS}
                      testid="field-delai"
                    />
                  </Fieldset>

                  <Fieldset legend="Vos coordonnées">
                    <Input
                      label="Nom & prénom *"
                      value={form.nom}
                      onChange={setField("nom")}
                      testid="field-nom"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        type="email"
                        label="Email *"
                        value={form.email}
                        onChange={setField("email")}
                        testid="field-email"
                      />
                      <Input
                        type="tel"
                        label="Téléphone *"
                        value={form.telephone}
                        onChange={setField("telephone")}
                        testid="field-telephone"
                      />
                    </div>
                    <Textarea
                      label="Précisions (optionnel)"
                      value={form.message}
                      onChange={setField("message")}
                      testid="field-message"
                    />
                  </Fieldset>

                  <button
                    type="submit"
                    data-testid="devis-submit-btn"
                    disabled={submitting}
                    className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#F08722] text-white px-10 py-5 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#D67518] transition-colors disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
                      </>
                    ) : (
                      <>
                        Envoyer la demande <ArrowUpRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-[#5C5E58]">
                    * Champs obligatoires. Vos données ne sont utilisées que
                    pour traiter votre demande.
                  </p>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Fieldset({ legend, children }) {
  return (
    <fieldset className="space-y-6">
      <legend className="text-xs uppercase tracking-[0.22em] font-semibold text-[#F08722] mb-2 flex items-center gap-3">
        <span className="w-6 h-px bg-[#F08722]" /> {legend}
      </legend>
      {children}
    </fieldset>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-[#5C5E58] mb-2">
        {label}
      </span>
      <input
        {...props}
        data-testid={testid}
        className="w-full bg-transparent border-b-2 border-[#D5D5D0] focus:border-[#F08722] focus:outline-none rounded-none px-0 py-3 text-base text-[#1A2D7E] placeholder:text-[#9A9A95]"
      />
    </label>
  );
}

function Textarea({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-[#5C5E58] mb-2">
        {label}
      </span>
      <textarea
        {...props}
        rows={4}
        data-testid={testid}
        className="w-full bg-transparent border-b-2 border-[#D5D5D0] focus:border-[#F08722] focus:outline-none rounded-none px-0 py-3 text-base text-[#1A2D7E] resize-none"
      />
    </label>
  );
}

function Select({ label, options, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-[#5C5E58] mb-2">
        {label}
      </span>
      <select
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-transparent border-b-2 border-[#D5D5D0] focus:border-[#F08722] focus:outline-none rounded-none px-0 py-3 text-base text-[#1A2D7E]"
      >
        <option value="">— Sélectionner —</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
